import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api/client";
import type { AnfrageListItem } from "../api/client";
import { useApi } from "../hooks/useApi";
import { useTenant } from "../app/TenantContext";
import { Icon } from "../shell/Icon";

/**
 * Inbox-Page im Redesign-Look: Hero + Stat-Strip + Anfrage-Liste mit
 * Hover-Akzentband, sticky Header.
 *
 * Positionen- und Score-Spalten sind aktuell Placeholder (—). Backend
 * liefert sie noch nicht im List-Endpoint — Folge-Sprint kann beide aus
 * Phase 3 (Konsolidierung) und Phase 4 (Match-Quote) anreichern.
 */

const STATUS_MAP: Record<string, { label: string; tone: "" | "ok" | "warn" | "bad" | "acc" }> = {
  eingegangen: { label: "EINGEGANGEN", tone: "" },
  in_pruefung: { label: "IN PRÜFUNG", tone: "acc" },
  angebot_erstellt: { label: "ANGEBOT BEREIT", tone: "acc" },
  versendet: { label: "VERSENDET", tone: "ok" },
  gewonnen: { label: "GEWONNEN", tone: "ok" },
  verloren: { label: "VERLOREN", tone: "bad" },
  verworfen: { label: "VERWORFEN", tone: "" },
};

export function InboxPage() {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const { state, refetch } = useApi(() => api.listAnfragen(tenantId), [tenantId]);
  const [filter, setFilter] = useState("");

  const anfragen = state.status === "ok" ? state.data : [];

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (q.length === 0) return anfragen;
    return anfragen.filter(
      (a) =>
        a.kunde.toLowerCase().includes(q) ||
        a.bauvorhaben.toLowerCase().includes(q) ||
        a.anfrageId.toLowerCase().includes(q)
    );
  }, [anfragen, filter]);

  const open = filtered.filter(
    (a) => a.status === "eingegangen" || a.status === "in_pruefung"
  ).length;
  const won = filtered.filter((a) => a.status === "gewonnen").length;

  return (
    <div className="screen fade-up">
      <div className="hero">
        <div className="hero-l">
          <div className="uppercase-mini">/ ANFRAGEN</div>
          <h1 className="hero-title">Inbox</h1>
          <div className="hero-meta mono">
            <span className="meta-val">{filtered.length}</span>
            <span className="meta-lbl">total</span>
            <span className="meta-sep">·</span>
            <span className="meta-val" style={{ color: "var(--acc)" }}>
              {open}
            </span>
            <span className="meta-lbl">offen</span>
            <span className="meta-sep">·</span>
            <span className="meta-val" style={{ color: "var(--ok)" }}>
              {won}
            </span>
            <span className="meta-lbl">gewonnen</span>
          </div>
        </div>
        <div className="hero-r">
          <div className="search-wrap">
            <span className="search-ico">
              <Icon name="search" size={15} />
            </span>
            <input
              type="text"
              className="input search-input"
              placeholder="Filter · Kunde / Bauvorhaben / ID"
              value={filter}
              onChange={(e) => setFilter(e.currentTarget.value)}
            />
            <span className="mono search-kbd">⌘F</span>
          </div>
          <Link to="/anfragen/neu" className="btn primary">
            <Icon name="plus" size={15} /> Neue Anfrage
          </Link>
          <button
            type="button"
            className="btn ghost"
            title="Neu laden"
            onClick={refetch}
          >
            <Icon name="refresh" size={15} />
          </button>
        </div>
      </div>

      <div className="stat-grid">
        <StatCard
          label="GESAMT"
          value={String(anfragen.length)}
          unit="anfragen"
          trend={anfragen.length > 0 ? `${anfragen.length}` : "—"}
          trendTone=""
        />
        <StatCard
          label="OFFEN"
          value={String(open)}
          unit="in bearbeitung"
          trend={open > 0 ? "aktiv" : "—"}
          trendTone={open > 0 ? "warn" : ""}
        />
        <StatCard
          label="ANGEBOT BEREIT"
          value={String(
            filtered.filter(
              (a) => a.status === "angebot_erstellt" || a.status === "versendet"
            ).length
          )}
          unit="zum versand"
          trend=""
          trendTone="ok"
        />
        <StatCard
          label="GEWONNEN"
          value={String(won)}
          unit="abgeschlossen"
          trend=""
          trendTone="ok"
        />
      </div>

      {state.status === "loading" && <ListSkeleton />}
      {state.status === "error" && <ErrorBanner error={state.error} onRetry={refetch} />}
      {state.status === "ok" && filtered.length === 0 && (
        <EmptyState filterActive={filter.length > 0} />
      )}
      {state.status === "ok" && filtered.length > 0 && (
        <AnfrageList items={filtered} onOpen={(id) => navigate(`/anfragen/${id}`)} />
      )}
    </div>
  );
}

function AnfrageList({
  items,
  onOpen,
}: {
  items: AnfrageListItem[];
  onOpen: (anfrageId: string) => void;
}) {
  return (
    <div className="anfrage-list">
      <div className="anfrage-list-head uppercase-mini">
        <div>Zeit</div>
        <div>Kunde · Bauvorhaben</div>
        <div className="num">Positionen</div>
        <div>Pfad</div>
        <div>Status</div>
        <div>Score</div>
        <div className="right">Anfrage-ID</div>
      </div>
      <div className="anfrage-list-body">
        {items.map((a, i) => {
          const status = STATUS_MAP[a.status] ?? { label: a.status.toUpperCase(), tone: "" };
          return (
            <button
              key={a.anfrageId}
              type="button"
              className="anfrage-row"
              style={{ animationDelay: `${i * 22}ms` }}
              onClick={() => onOpen(a.anfrageId)}
            >
              <div className="row-time">
                <div className="time-day mono">{formatDay(a.eingangsDatum)}</div>
                <div className="time-hh mono">{formatTime(a.eingangsDatum)}</div>
              </div>
              <div className="row-customer">
                <div className="cust-name">{a.kunde}</div>
                <div className="cust-bv mono">{a.bauvorhaben}</div>
              </div>
              <div className="num mono dim">—</div>
              <div>
                {a.pfad ? (
                  <span className="pfad-tag mono">{a.pfad}</span>
                ) : (
                  <span style={{ color: "var(--fg-faint)" }}>—</span>
                )}
              </div>
              <div>
                <span className={`pill ${status.tone}`}>
                  <span className="dot" />
                  {status.label}
                </span>
              </div>
              <div className="row-score">
                <span className="mono dim">—</span>
              </div>
              <div className="row-id mono">{a.anfrageId}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  unit,
  trend,
  trendTone,
}: {
  label: string;
  value: string;
  unit: string;
  trend: string;
  trendTone: "" | "ok" | "warn";
}) {
  return (
    <div className="stat-card">
      <div className="stat-head uppercase-mini">{label}</div>
      <div className="stat-row">
        <div className="stat-val mono">{value}</div>
        {trend.length > 0 && <div className={`stat-trend ${trendTone}`}>{trend}</div>}
      </div>
      <div className="stat-row stat-bot">
        <div className="stat-unit uppercase-mini" style={{ letterSpacing: "0.1em" }}>
          {unit}
        </div>
      </div>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="anfrage-list">
      <div className="anfrage-list-head uppercase-mini">
        <div>Zeit</div>
        <div>Kunde · Bauvorhaben</div>
        <div className="num">Positionen</div>
        <div>Pfad</div>
        <div>Status</div>
        <div>Score</div>
        <div className="right">Anfrage-ID</div>
      </div>
      <div className="anfrage-list-body">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className="anfrage-row"
            style={{
              opacity: 0.4,
              animation: "none",
              cursor: "default",
              background: "var(--bg-raised)",
            }}
          >
            <div className="row-time">
              <div className="time-day mono">···</div>
              <div className="time-hh mono">··:··</div>
            </div>
            <div className="row-customer">
              <div className="cust-name">Lade …</div>
            </div>
            <div className="num mono dim">—</div>
            <div className="mono dim">—</div>
            <div>
              <span className="pill">
                <span className="dot" />
                ···
              </span>
            </div>
            <div className="row-score">
              <span className="mono dim">—</span>
            </div>
            <div className="row-id mono">···</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EmptyState({ filterActive }: { filterActive: boolean }) {
  return (
    <div className="surface" style={{ padding: "60px 40px", textAlign: "center" }}>
      <div className="placeholder-text">
        <div className="placeholder-h">
          {filterActive ? "Keine Treffer" : "Noch keine Anfragen"}
        </div>
        <div className="placeholder-p">
          {filterActive
            ? "Filter anpassen oder zurücksetzen."
            : "Sobald Anfragen eingehen, erscheinen sie hier."}
        </div>
      </div>
    </div>
  );
}

function ErrorBanner({ error, onRetry }: { error: Error; onRetry: () => void }) {
  return (
    <div
      className="block"
      style={{
        borderColor: "oklch(0.72 0.18 25 / 0.4)",
        background:
          "linear-gradient(180deg, oklch(0.72 0.18 25 / 0.08), var(--bg-base) 80%)",
      }}
    >
      <div className="block-head">
        <span className="uppercase-mini" style={{ color: "var(--bad)" }}>
          // BACKEND NICHT ERREICHBAR
        </span>
      </div>
      <div style={{ display: "grid", gap: 10, color: "var(--fg-mute)", fontSize: 13 }}>
        <div className="mono" style={{ color: "var(--bad)" }}>
          {error.message}
        </div>
        <div className="dim">
          Tipp: Backend starten via{" "}
          <code
            className="mono"
            style={{
              background: "var(--bg-elevated)",
              padding: "2px 6px",
              borderRadius: 4,
            }}
          >
            pnpm --filter @bidi-handel/backend run dev
          </code>
        </div>
        <div>
          <button type="button" className="btn" onClick={onRetry}>
            <Icon name="refresh" size={14} /> Nochmal versuchen
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDay(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date();
  yest.setDate(today.getDate() - 1);
  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (sameDay(d, today)) return "HEUTE";
  if (sameDay(d, yest)) return "GESTERN";
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}
